/**
 * BatchCard Component
 *
 * Displays batch summary in a card layout
 * Shows: name, statistics, progress bar, status badge, actions
 */

import React from 'react';
import { Batch } from '../../types/batch';
import { ProgressBar } from '../ui/ProgressBar';

interface BatchCardProps {
  batch: Batch;
  onViewResults: (id: string) => void;
  onManageJobs?: (id: string) => void;
}

const statusStyles: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800 border-amber-300',
  running: 'bg-blue-100 text-blue-800 border-blue-300',
  completed: 'bg-green-100 text-green-800 border-green-300',
  failed: 'bg-red-100 text-red-800 border-red-300',
  partial: 'bg-purple-100 text-purple-800 border-purple-300',
};

export const BatchCard: React.FC<BatchCardProps> = ({ batch, onViewResults, onManageJobs }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const statusClass = statusStyles[batch.status] || statusStyles.pending;

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow p-6 cursor-pointer"
      onClick={() => onManageJobs?.(batch.batch_id)}
    >
      {/* Header with name and status */}
      <div className="flex justify-between items-start mb-3">
        <h3 className="text-lg font-semibold text-gray-900 flex-1 mr-4">
          {batch.name}
        </h3>
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusClass}`}
          role="status"
          aria-label={`Status: ${batch.status}`}
        >
          {batch.status}
        </span>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-900">{batch.total_jobs}</p>
          <p className="text-xs text-gray-500">Total</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-green-600">{batch.completed_jobs}</p>
          <p className="text-xs text-gray-500">Completed</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-red-600">{batch.failed_jobs}</p>
          <p className="text-xs text-gray-500">Failed</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <ProgressBar current={batch.completed_jobs} total={batch.total_jobs} />
      </div>

      {/* Success Rate */}
      <div className="mb-4">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700">Success Rate</span>
          <span className="text-sm font-bold text-gray-900">
            {batch.total_jobs > 0 ? `${Math.round(batch.success_rate * 100)}%` : 'N/A'}
          </span>
        </div>
      </div>

      {/* Dates */}
      <div className="text-xs text-gray-500 mb-4 space-y-1">
        <div>Created: {formatDate(batch.created_at)}</div>
        {batch.started_at && <div>Started: {formatDate(batch.started_at)}</div>}
        {batch.completed_at && <div>Completed: {formatDate(batch.completed_at)}</div>}
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onManageJobs?.(batch.batch_id);
          }}
          className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          aria-label={`Manage jobs for ${batch.name}`}
        >
          Manage Jobs
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onViewResults(batch.batch_id);
          }}
          className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
          aria-label={`View results for ${batch.name}`}
        >
          Results
        </button>
      </div>
    </div>
  );
};
